/**
 * ThumbnailSelectionDialog Component
 *
 * Dialog for selecting a thumbnail image for a class.
 * Features:
 * - Grid of all class images
 * - Highlight current thumbnail
 * - Click to select new thumbnail
 * - Auto-select button
 * - Preview of selected thumbnail
 */

import React, { useState, useEffect } from 'react'
import { X, Wand2, Check } from 'lucide-react'
import { apiService } from '@/services/api'
import { useCategoryContext } from '@/contexts/CategoryContext'

interface ThumbnailSelectionDialogProps {
  isOpen: boolean
  onClose: () => void
  classId: string
  className: string
}

interface ClassImage {
  id: string
  fileName: string
  imageUrl: string
  size: number
  lastModified: string
}

export const ThumbnailSelectionDialog: React.FC<ThumbnailSelectionDialogProps> = ({
  isOpen,
  onClose,
  classId,
  className,
}) => {
  const { getClassThumbnail, refreshThumbnailForClass } = useCategoryContext()
  const [images, setImages] = useState<ClassImage[]>([])
  const [currentThumbnail, setCurrentThumbnail] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load images when dialog opens
  useEffect(() => {
    if (isOpen && classId) {
      loadImages()
      loadCurrentThumbnail()
    }
  }, [isOpen, classId])

  const loadImages = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiService.getClassImages(classId)
      setImages(data)
    } catch (err) {
      console.error('Failed to load class images:', err)
      setError('Failed to load images')
    } finally {
      setLoading(false)
    }
  }

  const loadCurrentThumbnail = () => {
    const thumbnail = getClassThumbnail(classId)
    if (thumbnail) {
      setCurrentThumbnail(thumbnail.path)
      setSelectedImage(thumbnail.path)
    }
  }

  const handleSelectImage = (fileName: string) => {
    setSelectedImage(fileName)
  }

  const handleSave = async () => {
    if (!selectedImage) {
      setError('Please select an image')
      return
    }

    setSaving(true)
    setError(null)

    try {
      await apiService.setClassThumbnail(classId, selectedImage)
      await refreshThumbnailForClass(classId)
      onClose()
    } catch (err) {
      console.error('Failed to set thumbnail:', err)
      setError('Failed to set thumbnail')
    } finally {
      setSaving(false)
    }
  }

  const handleAutoSelect = async () => {
    setSaving(true)
    setError(null)

    try {
      const thumbnail = await apiService.autoSelectThumbnail(classId)
      setSelectedImage(thumbnail.path)
      setCurrentThumbnail(thumbnail.path)
      await refreshThumbnailForClass(classId)
    } catch (err) {
      console.error('Failed to auto-select thumbnail:', err)
      setError('Failed to auto-select thumbnail')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-xl font-semibold">Select Thumbnail</h2>
            <p className="text-sm text-gray-600">{className}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading images...</div>
            </div>
          ) : images.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">No images found in this class</div>
            </div>
          ) : (
            <>
              {/* Auto-select button */}
              <div className="mb-4">
                <button
                  onClick={handleAutoSelect}
                  disabled={saving}
                  className="btn-outline flex items-center gap-2"
                >
                  <Wand2 className="h-4 w-4" />
                  Auto-select (first image)
                </button>
              </div>

              {/* Image grid */}
              <div className="grid grid-cols-4 gap-4">
                {images.map((image) => {
                  const isSelected = selectedImage === image.fileName
                  const isCurrent = currentThumbnail === image.fileName

                  return (
                    <div
                      key={image.id}
                      onClick={() => handleSelectImage(image.fileName)}
                      className={`
                        relative cursor-pointer rounded-lg overflow-hidden
                        transition-all
                        ${
                          isSelected
                            ? 'border-[6px] border-solid border-primary-500 shadow-lg scale-105'
                            : isCurrent
                            ? 'border-4 border-solid border-green-500'
                            : 'border-2 border-gray-200 hover:border-gray-300'
                        }
                      `}
                    >
                      {/* Image */}
                      <div className="aspect-square bg-gray-100">
                        <img
                          src={image.imageUrl}
                          alt={image.fileName}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Selected indicator */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-primary-500 text-white rounded-full p-1">
                          <Check className="h-4 w-4" />
                        </div>
                      )}

                      {/* Current thumbnail badge */}
                      {isCurrent && !isSelected && (
                        <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                          Current
                        </div>
                      )}

                      {/* Filename */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2 truncate">
                        {image.fileName}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedImage && (
              <>
                Selected: <span className="font-medium">{selectedImage}</span>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-outline">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !selectedImage}
              className="btn-primary"
            >
              {saving ? 'Saving...' : 'Set as Thumbnail'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ThumbnailSelectionDialog
